import { clamp, random, round, sample } from 'lodash';
import Color from 'colorjs.io';
import chroma from 'chroma-js';

const uglyColors = [
    chroma('rgba(137,181,44,1)'),
    chroma('rgba(138,154,91,1)'),
    chroma('rgba(174,203,60,1)'),
    chroma('rgba(171,179,65,1)'),
    chroma('rgba(168,156,63,1)'),
    chroma('rgba(108,152,44,1)'),
    chroma('rgba(2,250,50,1)'),
    chroma('rgba(188,225,57,1)'),
    chroma('rgb(31, 116, 27)'),
    chroma('rgba(174,232,25,1)'),
    chroma('rgba(42,10,64,1)'),
    chroma('rgb(219, 89, 17)'),
    chroma('rgba(129,101,68,1)'),
    chroma('rgba(76,131,9,1)'),
    chroma('#b2bc0f'),
    chroma('#b2bc0f'),
    chroma('#70980f'),
    chroma('#865e22'),
    chroma('#d08723'),
    chroma('#484602'),
    chroma('#b87561'),
    chroma('#753d4d'),
    chroma('#4a412a')
]

const isCloseToUglyColor = (color: chroma.Color, threshold = 15) => {
    threshold = clamp(threshold, 0, 100);
    for (let i = 0; i < uglyColors.length; i++){
        if (chroma.deltaE(uglyColors[i], color) <= threshold){
            return true;
        }
    }
    return false;
}

/**
 * Gets a color which is not super close to green.
 */
const getRandomNonUglyColorExpensive = () : chroma.Color => {
    let color = chroma.random();
    if (isCloseToUglyColor(color)){
        return getRandomNonUglyColorExpensive();
    }
    return color;
}

let green = chroma('lime');
const getRandomNonUglyColor = () : chroma.Color => {
    let color = chroma.random();
    if (color.luminance() <= .3 || chroma.deltaE(green, color) <= 15){
        return getRandomNonUglyColor();
    }
    return color;

    // let phaserColor = Phaser.Display.Color.RandomRGB();
    // let color = new Color(phaserColor.rgba);
    // if (isCloseToUglyColor(color)){
    //     console.log("TOO UGLY");
    //     return getRandomNonUglyColor();
    // }
    // return color;
}

const _darkenMod = (color: chroma.Color) => color.darken(round(random(.1, 3, true), 2));
const _brightenMod = (color: chroma.Color) => color.brighten(round(random(.1, 3, true), 2));
const _saturateMod = (color: chroma.Color) => color.saturate(round(random(.1, 3, true), 2));
const _desaturatedMod = (color: chroma.Color) => color.desaturate(round(random(.1, 3, true), 2));
const _luminanceMod = (color: chroma.Color) => color.luminance(round(random(.5, 1, true), 2));
const applyRandomColorMods = (color: chroma.Color) => {
    //let c = chroma(color);
        //color.darken(round(random(.1, 3, true), 2))
   // let amt = round(random(.1, 3, true), 2);
    //console.log('Applying MOD:', color);
    //sample([c.darken, c.brighten])?.(round(random(.1, 3, true), 2)); //Random lightness modifier
    //sample([color.saturate, color.desaturate])?.(round(random(.1, 3, true), 2)) //Random saturation
    return sample([
        //_darkenMod, 
        _brightenMod,
        _saturateMod, 
        _desaturatedMod, 
        _luminanceMod
    ])!(color);
}

const deltaEInRange = (c1: chroma.Color | string, c2: chroma.Color | string, min: number, max: number) => {
    let distance = chroma.deltaE(c1, c2);
    return distance > min && distance < max;
}

/**
 * Returns a random choma color similar or no similar to the given color, depending on the range given. The 
 * scale is 0-100 and internally this maps to a deltaE difference of 0-55.
 * 
 * http://zschuessler.github.io/DeltaE/learn/#toc-introduction
 * 
 * NOTE: Seemingly getting random colors is very unlikely to result in a color with a deltaE difference above 60 or so. This method
 * takes in a 0-100 value but actually rescales it so that the max deltaE difference is really somewhere between 0 and 55. This still
 * requires many iterations.
 * 
 * @param testColorRgb 
 * @param minDistanceLab
 */
const getColorWithSimilarityInRange = (color1: chroma.Color | string, minDistance: number = 0, maxDistance: number = 100, limitIterations: boolean = true, _iterations: number = 0) : chroma.Color => {
    if (limitIterations && _iterations >= 500) {
        console.warn(`getColorWithSimilarityInRange(): More than 500 iterations necessary to retrieve desired color at deltaE min=${minDistance} and max=${maxDistance}.`);
        return chroma.random();
    }

    let min = round((clamp(minDistance, 0, 100) / 100) * 50, 4);
    let max = round((clamp(maxDistance, 0, 100) / 100) * 50, 4);
    if (min >= max){
        throw new Error('Min distance must be less than max distance');
    }

    let color2 = getRandomNonUglyColor();
    //let color2 = chroma.random(); //chroma.random();//avoidUglyColors ? getRandomNonUglyColor() : chroma.random();
    //let distance = chroma.deltaE(color1, color2);
    let inRange = deltaEInRange(color1, color2, min, max);
    if (!inRange){
        // color2 = applyRandomColorMods(color2);
        // inRange = deltaEInRange(color1, color2, min, max);
        // if (!inRange){
            return getColorWithSimilarityInRange(color1, minDistance, maxDistance, limitIterations, _iterations + 1);
        //}
    }

    //console.log(`Color found in ${_iterations} iterations.`);
    return color2;
}

// const getRandomNonUglyColor = () : chroma.Color => {
//     let color = chroma.random();
//     if (color.luminance() <= .3){
//         return getRandomNonUglyColor();
//     }
//     return color;
// }

const getContrastColorRgb = (r: number, g: number, b: number) => {
    var yiq = ((r*299)+(g*587)+(b*114)) / 1000;
    return (yiq >= 128) ? 'black' : 'white';
}

/**
 * Given a chroma color or rgb values, returns the contrast color (black or white) that will be most readable against 
 * the given color.
 */
function getContrastColor(color: chroma.Color | string | number, g?: number, b?: number) : string  {
    let c : chroma.Color;
    if (typeof color === 'number' && typeof g === 'number' && typeof b === 'number'){
        c = chroma.rgb(color, g, b)
    } else {
        c = chroma(color);
    }
    return  getContrastColorRgb(...c.rgb());
}

const interpolate = (color1: chroma.Color, color2: chroma.Color, steps: number) : chroma.Color[] => {
    return chroma.scale([color1, color2]).colors(steps).map(c => chroma(c));
}


function generateGradientSquare(size: number, colors: chroma.Color[]): chroma.Color[][] {
    const square: chroma.Color[][] = [];
    const gradient = chroma.scale(colors).mode('lab').colors(size);
    for (let x = 0; x < size; x++) {
        square[x] = [];
        for (let y = 0; y < size; y++) {
            const color = chroma.mix(
                chroma.mix(colors[0], colors[1], x / (size - 1)),
                chroma.mix(colors[2], colors[3], x / (size - 1)),
                y / (size - 1)
            );
            square[x][y] = color;
        }
    }
    return square;
}

function generateGradientSquareHex(size: number, colors: chroma.Color[]): { hex: string }[][] {
    const square: { hex: string }[][] = [];
    const gradient = chroma.scale(colors).mode('lab').colors(size);
    for (let x = 0; x < size; x++) {
        square[x] = [];
        for (let y = 0; y < size; y++) {
            const color = chroma.mix(
                chroma.mix(colors[0], colors[1], x / (size - 1)),
                chroma.mix(colors[2], colors[3], x / (size - 1)),
                y / (size - 1)
            );
            square[x][y] = { hex: color.hex() };
        }
    }
    return square;
}

// const mixColors = (colors: chroma.Color[]) => { 
//     return chroma.average(colors);
//     //return colors.slice(1).reduce<chroma.Color>((acc, curr) => acc.mix(curr), colors[0]) ?? chroma('white');
// }

/**
 * Given a color and a count, generates a set of colors for this given count that, when mixed together,
 * will equal the given color.
 */
function getRandomColorMix(count: number): { target: chroma.Color, mix: chroma.Color[] } {
    let mix: chroma.Color[] = [];
    for (let i = 0; i < count; i++) {
        mix.push(getRandomNonUglyColor());
    }
    return { target: chroma.average(mix, 'lch'), mix };
}

export {
    getRandomNonUglyColor,
    getRandomNonUglyColorExpensive,
    getColorWithSimilarityInRange,
    getContrastColor,
    interpolate,
    deltaEInRange,
    generateGradientSquare,
    generateGradientSquareHex,
    getRandomColorMix
}